import localeLookup from '../../../config/locale';
import {
  CHAMP_ID,
  DELETED_GROUP_ID,
  DELETED_PERSON_ID,
  EMPTY_DATE,
  EMPTY_ID,
  EXPERT_ID,
  KNOWLEDGE_OWNER_ID,
  MENTOR_ID,
  NONE_ID,
  PERSON_STATES,
  SELF_STUDY_ID,
} from '../../../constants';
import { store } from '../../../store/storeInstance';
import { getElementContentIcon } from '../../../utils/elementHelpers';
import { formatDate } from '../../../utils/helpers';
const latestActivityLabels = {
  AdminApproval: 'translations.Admin completion',
  AdminResetStatus: 'translations.Admin reset',
  ResetStatus: 'translations.ResetEvent',
  Standard: 'translations.Completion',
};

const statusFilterDefaultState = {
  noCompletion: true,
  validCompletion: true,
  validCompletionExpiring: true,
  invalidCompletionExpired: true,
  invalidCompletionOngoing: true,
};

const elementConfigurationDefaultState = {
  critical: true,
  notCritical: true,
  requiresSignature: true,
  doesNotRequireSignature: true,
  multipart: true,
  notMultipart: true,
  withExpiration: true,
  withoutExpiration: true,
};

const otherFiltersDefaultState = {
  activePersons: true,
  archivedPersons: false,
  currentlyConnected: true,
  notConnected: false,
};

export const filterDefaultState = {
  status: statusFilterDefaultState,
  elementConfiguration: elementConfigurationDefaultState,
  otherFilters: otherFiltersDefaultState,
  organisationUnits: { deselected: [] },
  roles: { deselected: [] },
  knowledgeAreas: { deselected: [] },
  knowledgeElements: { deselected: [] },
  persons: { deselected: [] },
  trainers: { deselected: [] },
  mentors: { deselected: [] },
};

const getDeselected = (selectedIds = [], allIds = []) =>
  selectedIds.length === 0
    ? []
    : allIds.filter((id) => !selectedIds.includes(id));

const getSelected = (deselectedIds = [], allIds = []) =>
  deselectedIds.length === 0
    ? []
    : allIds.filter((id) => !deselectedIds.includes(id));

export const getFilterStateWithAppliedFilter = ({
  filter,
  personIds,
  organisationUnitIds,
  roleIds,
  areaIds,
  knowledgeElementIds,
  groupIds,
  wildcardPersonIds,
  trainerIds,
  mentorIds,
}) => ({
  ...filterDefaultState,
  status: {
    ...filterDefaultState.status,
    ...filter.status,
  },
  elementConfiguration: {
    ...filterDefaultState.elementConfiguration,
    ...filter.elementConfiguration,
  },
  otherFilters: {
    ...filterDefaultState.otherFilters,
    ...filter.otherFilters,
  },
  organisationUnits: {
    deselected: getDeselected(filter.organisationUnits, organisationUnitIds),
  },
  roles: {
    deselected: getDeselected(filter.roles, roleIds),
  },
  knowledgeAreas: {
    deselected: getDeselected(filter.knowledgeAreas, areaIds),
  },
  knowledgeElements: {
    deselected: getDeselected(filter.knowledgeElements, knowledgeElementIds),
  },
  persons: {
    deselected: getDeselected(filter.persons, personIds),
  },
  trainers: {
    deselected: getDeselected(
      filter.mediators,
      trainerIds || [
        EMPTY_ID,
        ...groupIds,
        ...wildcardPersonIds.filter(
          (id) => ![SELF_STUDY_ID, DELETED_GROUP_ID, CHAMP_ID].includes(id)
        ),
        ...personIds,
      ]
    ),
  },
  mentors: {
    deselected: getDeselected(
      filter.mentors,
      mentorIds || [
        EMPTY_ID,
        NONE_ID,
        DELETED_PERSON_ID,
        DELETED_GROUP_ID,
        ...groupIds,
        ...wildcardPersonIds.filter(
          (id) =>
            ![EMPTY_ID, NONE_ID, DELETED_PERSON_ID, DELETED_GROUP_ID].includes(
              id
            )
        ),
        ...personIds,
      ]
    ),
  },
});

export const getInvertedFilter = (
  filters,
  {
    personIds,
    organisationUnitIds,
    roleIds,
    areaIds,
    knowledgeElementIds,
    trainerIds,
    mentorIds,
  }
) => ({
  status: filters.status,
  elementConfiguration: filters.elementConfiguration,
  otherFilters: filters.otherFilters,
  organisationUnits: getSelected(
    filters.organisationUnits.deselected,
    organisationUnitIds
  ),
  roles: getSelected(filters.roles.deselected, roleIds),
  knowledgeAreas: getSelected(filters.knowledgeAreas.deselected, areaIds),
  knowledgeElements: getSelected(
    filters.knowledgeElements.deselected,
    knowledgeElementIds
  ),
  persons: getSelected(filters.persons.deselected, personIds),
  mediators: getSelected(filters.trainers.deselected, trainerIds),
  mentors: getSelected(filters.mentors.deselected, mentorIds),
});

const isFilterDefault = (filterSection, defaultSection) =>
  Object.keys(filterSection).every(
    (key) => filterSection[key] === defaultSection[key]
  );

export const isAnyFiltersActive = (filters, { organisationUnitIds }) =>
  Object.keys(filters).some((key) => {
    if (key === 'status') {
      return !isFilterDefault(filters[key], statusFilterDefaultState);
    }

    if (key === 'elementConfiguration') {
      return !isFilterDefault(filters[key], elementConfigurationDefaultState);
    }

    if (key === 'otherFilters') {
      return !isFilterDefault(filters[key], otherFiltersDefaultState);
    }

    if (key === 'organisationUnits') {
      return filters[key].deselected.length !== 0;
    }

    return filters[key].deselected.length !== 0;
  });

const getArchivedSuffix = (state) =>
  state === PERSON_STATES.ARCHIVED
    ? ` (${localeLookup('translations.Archived')})`
    : '';

const getSingleOrCountLabel = (items = [], singleLabel, pluralLabel) => {
  if (items.length > 1) {
    return `${items.length} ${pluralLabel}`;
  }

  if (items.length === 1) {
    return singleLabel(items[0]);
  }

  return null;
};

const getAggregatedTooltip = (items = [], itemLabel) =>
  items.length > 1 ? items.map(itemLabel).filter(Boolean).join('\n') : '';

const getResponsibleLabel = (record) => {
  const responsibleName = record?.responsible?.name || record?.responsibleName;

  if (!responsibleName) {
    return null;
  }
  return `${responsibleName}${getArchivedSuffix(record?.responsible?.state)}`;
};

const getResponsibleSubtitle = (record) =>
  record?.pending && record.pending !== 'None' && getPersonName(record)
    ? `& ${getPersonName(record)}`
    : '';

const getResponsibleSubtitleTooltip = (record) =>
  record?.pending && record.pending !== 'None'
    ? getPersonTooltip(record.person)
    : '';

const getResponsibleTitleColor = (record) =>
  record?.responsible?.showError ||
  record?.responsible?.id === DELETED_PERSON_ID ||
  record?.responsible?.state === PERSON_STATES.ARCHIVED ||
  record?.responsibleMissing
    ? 'red'
    : undefined;

const getSingleMentor = (record) =>
  record?.mentors?.length === 1 ? record.mentors[0] : null;

const getMentorLabel = (record) =>
  getSingleOrCountLabel(
    record?.mentors || [],
    (mentor) =>
      `${mentor.name || ''}${
        mentor?.suffix ? ` ${mentor.suffix}` : ''
      }${getArchivedSuffix(mentor.state)}`,
    localeLookup('translations.mentors')
  );

const getMentorAggregateTooltip = (record) =>
  getAggregatedTooltip(
    record?.mentors || [],
    (mentor) =>
      `${mentor.name || ''}${
        mentor?.suffix ? ` ${mentor.suffix}` : ''
      }${getArchivedSuffix(mentor.state)}`
  );

const getMentorTitleColor = (record) =>
  getSingleMentor(record)?.showError ||
  getSingleMentor(record)?.state === PERSON_STATES.ARCHIVED
    ? 'red'
    : undefined;

const getMentorTooltip = (record) => getPersonTooltip(getSingleMentor(record));

const getOrganisationUnitLabel = (record) =>
  getSingleOrCountLabel(
    record?.organisationUnitNames || [],
    (name) => name,
    localeLookup('translations.Organisation units')
  );

const getOrganisationUnitTooltip = (record) =>
  getAggregatedTooltip(record?.organisationUnitNames || [], (name) => name);

const getRoleLabel = (record) =>
  getSingleOrCountLabel(
    record?.roleNames || [],
    (name) => name,
    localeLookup('translations.Roles')
  );

const getRoleTooltip = (record) =>
  getAggregatedTooltip(record?.roleNames || [], (name) => name);

const getLatestActivityInfo = (record) => {
  const latestActivity = record?.latestActivity;

  if (!latestActivity?.hasActivity) {
    return { latestActivity: '', latestActivitySubtitle: '' };
  }

  const latestActivityTitle =
    localeLookup(latestActivityLabels[latestActivity.type]) ||
    latestActivity.type;
  const signedByName = latestActivity?.signedByPerson?.name;
  const signedAt = latestActivity?.signedAt;
  const timeZone = store.getState().rootmenu?.timeZone || null;

  const formattedSignedAt = signedAt
    ? formatDate(signedAt, timeZone, true) || signedAt
    : '';

  return {
    latestActivity: latestActivityTitle,
    latestActivitySubtitle:
      signedByName && formattedSignedAt
        ? `${signedByName}, ${formattedSignedAt}`
        : '',
    latestActivitySubtitleTooltip: getPersonTooltip(
      latestActivity?.signedByPerson
    ),
  };
};

const getExpirationDateLabel = (record) => {
  if (
    !record?.expirationDate ||
    record.expirationDate.startsWith('0001-01-01')
  ) {
    return '';
  }
  const timeZone = store.getState().rootmenu?.timeZone || null;

  return (
    formatDate(record.expirationDate, timeZone, true) || record.expirationDate
  );
};

const getPersonName = (record) =>
  record.personName || record?.person?.name || '';

const getPersonSubtitle = (record) =>
  record?.person?.state === 'archived' || record?.person?.isActive === false
    ? localeLookup('translations.Archived')
    : '';

const getPersonTooltip = (person) =>
  person?.initials
    ? `${person.initials}${
        person.employeeNumber ? ` · ${person.employeeNumber}` : ''
      }`
    : '';

const getElementIcon = (record) => {
  const iconFromContent = getElementContentIcon({
    description: record.description,
    files: record.files,
  });

  if (iconFromContent) {
    return iconFromContent;
  }

  if (record.contentType === 'Image') {
    return 'picture';
  }

  if (record.contentType === 'Video') {
    return 'camera';
  }

  return null;
};

export const mapPersonElementToRow = (record) => ({
  id: record.id || `${record.elementId}${record.personId}`,
  areaId: record.areaId,
  contentType: record.contentType,
  daysUntilExpiration: record.daysUntilExpiration,
  elementId: record.elementId,
  elementIcon: getElementIcon(record),
  personId: record.personId,
  elementName: record.elementName,
  hasHistory: record.hasHistory,
  hasValidity:
    (record.expirationDate &&
      record.expirationDate !== '' &&
      record.expirationDate !== EMPTY_DATE) ||
    record.status === 'NoCompletionWithExpiration',
  personName: getPersonName(record),
  personSubtitle: getPersonSubtitle(record),
  personTooltip: getPersonTooltip(record.person),
  nextAction: record.nextAction,
  pending: record.pending,
  responsibleId: record.responsibleId,
  responsible: getResponsibleLabel(record),
  responsibleSubtitle: getResponsibleSubtitle(record),
  responsibleSubtitleTooltip: getResponsibleSubtitleTooltip(record),
  responsibleTitleColor: getResponsibleTitleColor(record),
  responsibleTooltip: getPersonTooltip(record.responsible),
  mentor: getMentorLabel(record) || record.mentorName || null,
  mentorTitleColor: getMentorTitleColor(record),
  mentorTooltip: getMentorAggregateTooltip(record) || getMentorTooltip(record),
  ...getLatestActivityInfo(record),
  expirationDate: getExpirationDateLabel(record),
  organisationUnit: getOrganisationUnitLabel(record),
  organisationUnitTooltip: getOrganisationUnitTooltip(record),
  role: getRoleLabel(record),
  roleTooltip: getRoleTooltip(record),
  module: record.moduleName || null,
  status: record.status,
  raw: record,
});

export { statusFilterDefaultState };
